import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../config/mapTile';
import pin2IconUrl from '../assets/pin2.svg';
import { useTranslation } from 'react-i18next';

const pickerIcon = new L.Icon({
  iconUrl: pin2IconUrl,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -32],
});

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const MapCenterUpdater = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat !== '' && lng !== '') {
      map.setView([parseFloat(lat), parseFloat(lng)], map.getZoom(), { animate: false });
    }
  }, [lat, lng, map]);
  return null;
};

const MapLocationPicker = ({ lat, lng, onChange }) => {
  const { t } = useTranslation('venueMap');
  const [isLocating, setIsLocating] = useState(false);
  const mapRef = useRef(null);
  const hasPin = lat !== '' && lng !== '';
  const center = hasPin ? [parseFloat(lat), parseFloat(lng)] : [35.681236, 139.767125];

  const handlePick = useCallback((newLat, newLng) => {
    onChange(newLat.toFixed(6), newLng.toFixed(6));
  }, [onChange]);

  const handleCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const lat = coords.latitude.toFixed(6);
        const lng = coords.longitude.toFixed(6);
        onChange(lat, lng);
        mapRef.current?.setView([coords.latitude, coords.longitude], 15, { animate: true });
        setIsLocating(false);
      },
      () => setIsLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onChange]);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={handleCurrentLocation} disabled={isLocating}
          style={{ fontSize: 13, padding: '4px 10px', borderRadius: 6, border: '1px solid #bbb', background: '#f9fafb', cursor: 'pointer' }}>
          {isLocating ? t('locating') : '📍 現在地を使用'}
        </button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>または地図をクリックして位置を指定</span>
        {hasPin && (
          <button type="button" onClick={() => onChange('', '')}
            style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
            ✕ 解除
          </button>
        )}
      </div>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: 220, borderRadius: 8, border: '1px solid #d1d5db' }}
        ref={mapRef}
      >
        <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
        <ClickHandler onPick={handlePick} />
        <MapCenterUpdater lat={lat} lng={lng} />
        {hasPin && (
          <Marker position={[parseFloat(lat), parseFloat(lng)]} icon={pickerIcon} />
        )}
      </MapContainer>
      {hasPin && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          緯度: {lat} / 経度: {lng}
        </div>
      )}
    </div>
  );
};

export default MapLocationPicker;
